import {
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import UtilityToolbarDownload from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDownload'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import {
  VirtualCardDeactivatingInFilter,
  VirtualCardStatusFilter,
  VirtualCardTypeFilter,
  VirtualCardUserFilter,
} from 'src/components/VirtualCardsBoxAndLinks/filters'
import type { VirtualCardsFilters } from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import type { VirtualCardsFiltersBody } from 'src/queries/virtualCards.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

export interface VirtualCardsListFilterProps {
  defaultTitle?: React.ReactElement
  loading: boolean
  hideFilters?: boolean
  hideKeyword?: boolean
  isYourCards?: boolean
  isDirty: boolean
  totalCount: number
  filteredCount?: number
  selectedFiltersCount: number
  filterValues: Partial<VirtualCardsFilters>
  apiQueryParams: VirtualCardsFiltersBody
  handleFilterChange: (filters: Partial<VirtualCardsFilters>) => void
  resetFilters: () => void
  resetSingleFilter: (key: keyof VirtualCardsFilters) => void
  onDownloadClick?: (filters: VirtualCardsFiltersBody) => void
  downloadPending?: boolean
}

const renderDivider = (): React.ReactElement => (
  <DividerLineDiv
    css={css`
      margin: 20px 0px;
    `}
  />
)

const VirtualCardsListFilter: React.FC<VirtualCardsListFilterProps> = ({
  defaultTitle,
  loading,
  hideFilters,
  hideKeyword,
  isYourCards,
  isDirty,
  totalCount,
  filteredCount = 0,
  selectedFiltersCount,
  filterValues,
  apiQueryParams,
  handleFilterChange,
  resetFilters,
  resetSingleFilter,
  onDownloadClick,
  downloadPending,
}): React.ReactElement => {
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const [resetAllFilters, setResetAllFilters] = useState(false)

  const toggleSetFilter = useCallback(
    (key: 'statuses' | 'recipients' | 'cardTypes', value: string): void => {
      const next = new Set(filterValues[key] ?? [])
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      handleFilterChange({ [key]: next })
    },
    [filterValues, handleFilterChange],
  )

  const onFiltersResetClick = (): void => {
    setResetAllFilters(true)
    resetFilters()
  }

  return (
    <FilterSection
      hideButtonChevron
      appliedFiltersCount={selectedFiltersCount}
      additionalFilterElement={
        onDownloadClick !== undefined ? (
          <UtilityToolbarDownload
            disabled={downloadPending}
            loading={downloadPending}
            onClick={(): void => onDownloadClick(apiQueryParams)}
          />
        ) : undefined
      }
      filtersFlyoutProps={
        !hideFilters
          ? {
              title: translate('Virtual Card Filters'),
              onReset: onFiltersResetClick,
              loading,
              filters: (
                <>
                  <VirtualCardStatusFilter
                    selectedStatuses={filterValues.statuses}
                    onToggleStatus={(status): void =>
                      toggleSetFilter('statuses', status)
                    }
                  />
                  {renderDivider()}
                  <VirtualCardTypeFilter
                    selectedCardTypes={filterValues.cardTypes}
                    onToggleCardType={(cardType): void =>
                      toggleSetFilter('cardTypes', cardType)
                    }
                  />
                  <VirtualCardDeactivatingInFilter
                    selectedDeactivatingIn={filterValues.deactivatingIn ?? ''}
                    onChange={(deactivatingIn): void =>
                      handleFilterChange({ deactivatingIn })
                    }
                  />
                  {!isYourCards && (
                    <>
                      {renderDivider()}
                      <VirtualCardUserFilter
                        isYourCards={isYourCards}
                        selectedRecipients={filterValues.recipients}
                        resetAllFilters={resetAllFilters}
                        setResetAllFilters={setResetAllFilters}
                        onToggleRecipient={(recipientId): void =>
                          toggleSetFilter('recipients', recipientId)
                        }
                        onResetRecipients={(): void =>
                          resetSingleFilter('recipients')
                        }
                      />
                    </>
                  )}
                </>
              ),
            }
          : undefined
      }
      topLeftContent={
        <div
          css={css`
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
          `}
        >
          {isDirty &&
            (loading ? (
              <Processing />
            ) : (
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {totalCount === 1
                  ? filteredCount === 1
                    ? translate(
                        '${filteredCount} Result (of ${totalCount} card)',
                        {
                          filteredCount: `${filteredCount}`,
                          totalCount: `${totalCount}`,
                        },
                      )
                    : translate(
                        '${filteredCount} Results (of ${totalCount} card)',
                        {
                          filteredCount: `${filteredCount}`,
                          totalCount: `${totalCount}`,
                        },
                      )
                  : filteredCount === 1
                    ? translate(
                        '${filteredCount} Result (of ${totalCount} cards)',
                        {
                          filteredCount: `${filteredCount}`,
                          totalCount: `${totalCount}`,
                        },
                      )
                    : translate(
                        '${filteredCount} Results (of ${totalCount} cards)',
                        {
                          filteredCount: `${filteredCount}`,
                          totalCount: `${totalCount}`,
                        },
                      )}
              </span>
            ))}
          {!isDirty && defaultTitle !== undefined && defaultTitle}
          {!hideKeyword && (
            <div
              css={css`
                flex-grow: 1;
                padding-left: ${isDirty || defaultTitle !== undefined
                  ? 40
                  : 0}px;
              `}
            >
              <KeywordFilter
                value={filterValues.search ?? ''}
                onChange={(value: string): void => {
                  handleFilterChange({ search: value })
                }}
                placeholder={translateSingleString(
                  locale,
                  'Name, Available Credit, Last 4, etc.',
                )}
              />
            </div>
          )}
        </div>
      }
    />
  )
}

export default VirtualCardsListFilter
